import * as React from 'react';

import { Link, useRouteMatch } from 'react-router-dom';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';

import { Country } from '@common/types/data/country';
import * as validators from '@webapp/utils/validators';
import { isPassword } from '@webapp/utils/validators/index';
import { ContactInfo } from '@webapp/types/common/contact-info';
import { normalizePhoneOrEmail } from '@webapp/utils/normalizers';
import BasicAccountData from '@webapp/types/auth/basic-account-data';

import PasswordCtrl from '../password';
import ErrorCtrl from '@webapp/controllers/error';
import EmailOrPhoneCtrl, {
  DEFAULT_COUNTRY,
} from '@webapp/controllers/form/email-or-phone';

import Grid from '@components/basics/grid';
import Flex from '@components/layout/flex';
import Hint from '@components/basics/hint';
import Button from '@components/actions/button';
import InputField from '@components/form/input';
import Typography from '@components/basics/typography';
import ButtonArrow from '@components/actions/arrow-button';
import AnimatedHintCtrl from '@webapp/controllers/animated-hint';

interface EmailPasswordCtrlProps {
  className?: string;
  action: React.ReactNode;
  screen: string;
  errorKey: string;
  error?: string;
  isLoading?: boolean;
  includeName?: boolean;
  onSubmit: (data: BasicAccountData) => void;
  hasInfo?: boolean;
  setHasInfo?: React.Dispatch<React.SetStateAction<boolean>>;
  setContactValue?: (email: string) => void;
  enablePhone?: boolean;
}

const processFormData = (
  ev: React.FormEvent<HTMLFormElement>,
  contact: ContactInfo | undefined,
  password: string,
  givenName: string,
  familyName: string,
  country: Country,
  includeName?: boolean,
  source?: string | null
): BasicAccountData | null => {
  ev.preventDefault();

  if (!(contact && validators.isPassword(password))) {
    return null;
  }

  if (includeName && !(givenName && familyName)) {
    return null;
  }

  const data: BasicAccountData = {
    username: contact.value,
    email: contact.type === 'email' ? contact.value : undefined,
    phone: contact.type === 'sms' ? contact.value : undefined,
    password,
    given_name: givenName,
    family_name: familyName,
    country,
    source,
  };

  if (!(data.email || data.phone)) {
    return null;
  }

  return data;
};

export const EmailPasswordCtrl: React.FC<EmailPasswordCtrlProps> = (props) => {
  const { enablePhone = false } = props;
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [contact, setContact] = React.useState<ContactInfo>();
  const [country, setCountry] = React.useState<Country>(DEFAULT_COUNTRY);
  const [password, setPassword] = React.useState('');
  const [tab, setTab] = React.useState<'name' | 'email'>('name');
  const [firstNameMissing, setFirstNameMissing] = React.useState(false);
  const [lastNameMissing, setLastNameMissing] = React.useState(false);
  const [contactMissing, setContactMissing] = React.useState(false);
  const [passwordMissing, setPasswordMissing] = React.useState(false);
  const [contactError, setContactError] = React.useState<string | null>(null);

  const match = useRouteMatch();

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  React.useEffect(() => {
    const account = window.location.search.match(/account=([^&]+)/);
    if (account) {
      const newContact = normalizePhoneOrEmail(account[1].trim());
      if (newContact) {
        setContact(newContact);
      }
    }
  }, []);

  React.useEffect(() => {
    if (props.includeName && props.hasInfo) {
      setTab('name');
    } else if (props.includeName && !props.hasInfo) {
      setTab('email');
    }
  }, [props.hasInfo, props.includeName]);

  const getErrorMessage = (id: string) => {
    switch (id) {
      case 'NotAuthorizedException':
        return <Translate id="NotAuthorizedException_TEXT" />;

      default:
        break;
    }
  };

  const handleContactFocus = () => {
    setContactMissing(false);
    setContactError(null);
  };

  return (
    <form
      className={props.className || ''}
      autoComplete="off"
      onSubmit={(ev) => {
        ev.preventDefault();

        // TODO: DEVTEAM-1057 - This needs to be reviwed, the user needs to input their name in order to go to the next screen.
        if (props.includeName && props.setHasInfo) {
          setFirstNameMissing(!firstName);
          setLastNameMissing(!lastName);
          props.setHasInfo(false);
        }

        if (!(props.includeName && props.hasInfo)) {
          setContactMissing(!contact?.value);
          setContactError('AUTH.EMAIL_MISSING');
          setPasswordMissing(!password);
        }

        if (
          !enablePhone &&
          contact?.value &&
          !emailRegex.test(contact?.value)
        ) {
          setContactMissing(true);
          setContactError('AUTH.VERIFY.INPUT.INVALID_EMAIL');
          return false;
        }

        if (!isPassword(password)) {
          return false;
        }
        const queryString = new URLSearchParams(window.location.search);
        const source = queryString.get('source');
        const formData = processFormData(
          ev,
          contact,
          password,
          firstName,
          lastName,
          country,
          props.includeName,
          source
        );

        if (!formData) {
          return false;
        }
        if (tab === 'name' && props.screen === 'registration') {
          return;
        }

        props.setContactValue && props.setContactValue(contact?.value || '');
        props.onSubmit(formData);
        setPassword('');
        return false;
      }}
    >
      <WrapperInputs className={props.errorKey ? 'has-error' : ''}>
        <InputsContainer
          alignItems="center"
          justifyContent="space-between"
          gap={10}
        >
          {tab === 'name' && props.screen === 'registration' ? (
            <Grid className="inputs-wrapper" gap={5}>
              <InputField
                value={firstName}
                isGrey
                label={<Translate id="REGISTER.FIRST_NAME" />}
                error={
                  firstNameMissing ? (
                    <Translate id="AUTH.FIRST_NAME_MISSING" />
                  ) : undefined
                }
                onChange={(value) => setFirstName(value)}
                onFocus={() => setFirstNameMissing(false)}
              />
              <InputField
                value={lastName}
                label={<Translate id="REGISTER.LAST_NAME" />}
                isGrey
                error={
                  lastNameMissing ? (
                    <Translate id="AUTH.LAST_NAME_MISSING" />
                  ) : undefined
                }
                onChange={(value) => setLastName(value)}
                onFocus={() => setLastNameMissing(false)}
              />
            </Grid>
          ) : (
            <Grid className="inputs-wrapper" gap={5}>
              <EmailOrPhoneCtrl
                value={contact?.value || ''}
                country={country}
                error={
                  contactMissing && contactError ? (
                    <Translate id={contactError} />
                  ) : undefined
                }
                onChange={(value) => setContact(value)}
                onCountryChange={(value) => setCountry(value)}
                onFocus={() => handleContactFocus()}
                enablePhone={enablePhone}
                isGrey
              />

              <PasswordCtrl
                password={password}
                onChange={setPassword}
                onFocus={() => setPasswordMissing(false)}
                error={
                  passwordMissing ? (
                    <Translate id="AUTH.PASSWORD_MISSING" />
                  ) : undefined
                }
                isGrey
              />
            </Grid>
          )}
          <ButtonArrow
            id="login"
            type="submit"
            className="button-arrow"
            isLoading={!!props.isLoading}
            disabled={
              props.screen === 'registration' && !firstName && !lastName
            }
          />
        </InputsContainer>
      </WrapperInputs>
      {props.screen === 'registration' && props.hasInfo && (
        <AnimatedHintCtrl
          customTitle={<Translate id="REGISTER.NAME.HINT.TITLE" />}
          className="mb-40"
        >
          <Translate id="REGISTER.NAME.HINT.TEXT" />
        </AnimatedHintCtrl>
      )}
      <ErrorCtrl
        className="mb-40"
        hasBackground
        type={props.errorKey}
        message={
          props.errorKey && props.error
            ? getErrorMessage(props.error)
            : undefined
        }
      />
      <div>
        {props.screen === 'login' ? (
          <LoginActions>
            <Separator />
            <Flex
              className="login-actions-wrapper"
              justifyContent="space-between"
              alignItems="center"
            >
              <Link className="link" to={`${match.path}/forgot-password`}>
                <Typography type="small" weight="400">
                  <Translate id="AUTH.LOGIN.FORGOT_PASSWORD" />
                </Typography>
              </Link>
              <Link className="button" to={`/register`}>
                <Button buttonType="secondary">
                  <Translate id="AUTH.LOGIN.CREATE_ACCOUNT" />
                </Button>
              </Link>
            </Flex>
            <Separator />
          </LoginActions>
        ) : !props.hasInfo ? (
          <Grid gap={3}>
            <Hint
              noBorderBottom
              noMargin
              customTitle={<Translate id="AUTH.LOGIN.PASSWORD.HINT.TITLE" />}
            >
              <Translate id="AUTH.LOGIN.PASSWORD.HINT.TEXT" />
            </Hint>
            <CustomHint>
              <Typography type="small" weight="700">
                <Translate id="REGISTER.TERMS.HINT" />{' '}
                <Link className="terms-link" to="/terms-of-service/">
                  <Translate id="REGISTER.TERMS.HINT.TERMS" />
                </Link>{' '}
                &{' '}
                <Link className="terms-link" to="/privacy-policy/">
                  <Translate id="REGISTER.TERMS.HINT.PRIVACY" />
                </Link>
                .
              </Typography>
            </CustomHint>
          </Grid>
        ) : null}
      </div>
    </form>
  );
};

export default styled(EmailPasswordCtrl)`
  .inputs-wrapper {
    width: 100%;
  }

  .button-arrow {
    max-width: 78px;
  }

  .terms-link {
    border-bottom: 1px solid ${({ theme }) => theme.colors.primary};
  }
`;

export const WrapperInputs = styled(Flex)`
  margin-bottom: 40px;
  width: 100%;

  &.has-error {
    margin-bottom: 20px;
  }
`;

const LoginActions = styled.div`
  width: 100%;

  .login-actions-wrapper {
    flex-direction: column-reverse;
    gap: 20px;

    @media ${({ theme }) => theme.device.tablet} {
      margin-bottom: 20px;
      flex-direction: row;
    }

    .button {
      width: 100%;
      @media ${({ theme }) => theme.device.tablet} {
        width: auto;
      }
      button {
        flex-grow: 1;
        width: 100%;
        @media ${({ theme }) => theme.device.tablet} {
          width: auto;
        }
      }
    }
  }
`;

const InputsContainer = styled(Flex)`
  width: 100%;
`;

const Separator = styled.span`
  width: 100%;
  height: 1px;
  margin-top: 1px;
  display: block;
  margin-block: 20px;
  background-color: ${({ theme }) => theme.colors.headerBorder};
  @media ${({ theme }) => theme.device.tablet} {
    display: none;
  }
`;

const CustomHint = styled.div`
  background: ${({ theme }) => theme.colors.hint};
  padding: 30px;
  border-radius: 0 0 10px 10px;
`;
